/**
 * Native / WebView Google Sign-In helper.
 *
 * Why this exists: `@react-oauth/google`'s <GoogleLogin/> iframe widget is
 * served by accounts.google.com/gsi/button and refuses to render inside any
 * WebView-based wrapper (Capacitor `capacitor://localhost`, Median.co WKWebView,
 * etc.) because those origins cannot be added to Authorized JavaScript Origins
 * in Google Cloud Console. So we skip the iframe entirely and drive an
 * Authorization Code flow against these backend endpoints:
 *
 *   GET /api/auth/google/authorize?platform=<mode>  -> 302 to Google consent
 *   GET /api/auth/google/callback                   -> exchanges code, then
 *      • platform=native        -> 302 to unityblaze://auth/native-callback?token=...
 *      • platform=web-redirect  -> 302 to <same-origin>/auth/native-callback?token=...
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH. The backend derives its redirect_uri from the request
 * host; we only need to open the /authorize endpoint here.
 */
import { Browser } from '@capacitor/browser';

import { API } from '@/App';
import { isNative, isMedianWebView } from '@/native/permissions';

/**
 * Kick off Google Sign-In. Behaviour depends on the wrapper:
 *   • Capacitor native (iOS/Android)  -> open @capacitor/browser (SFSafariView/Custom Tabs);
 *                                        callback returns via unityblaze:// deep link.
 *   • Median.co WebView               -> full-window navigation to /authorize; callback
 *                                        returns via a same-origin HTTPS URL that the
 *                                        WebView loads as a regular page.
 *   • Plain browser (shouldn't hit)   -> fall back to same-window navigation.
 */
export async function startNativeGoogleLogin() {
  if (isNative()) {
    const url = `${API}/auth/google/authorize?platform=native`;
    await Browser.open({ url, presentationStyle: 'fullscreen' });
    return;
  }

  // Median.co (and any other plain WebView wrapper) — do a full-window
  // navigation. The backend callback will hand us back on the same origin
  // via /auth/native-callback?token=..., which React Router will pick up.
  if (isMedianWebView()) {
    window.location.href = `${API}/auth/google/authorize?platform=web-redirect`;
    return;
  }

  // Safety fallback — shouldn't be reached because we only render the native
  // button when isWebViewApp() is true. If we do get here, use redirect mode
  // (works even without a WebView bridge).
  window.location.href = `${API}/auth/google/authorize?platform=web-redirect`;
}

/**
 * Called by the deep-link handler (Capacitor only) once the OS opens the app
 * back with `unityblaze://auth/native-callback?token=...`. Closes the in-app
 * browser sheet so the user lands directly on our React screen. No-op on
 * Median (there was never an in-app browser to close — the WebView navigated
 * itself) and on any platform where Browser.close() throws.
 */
export async function closeAuthBrowser() {
  try {
    await Browser.close();
  } catch {
    // Some platforms throw if there's nothing to close — safe to ignore.
  }
}
